import React, { useState, useCallback, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import Spinner from "../loading/spinner";
import ModalSelect from "./modalSelect";
import { toggleState } from "../../specialMethod/toggleState";

const getSchedules = () => JSON.parse(localStorage.getItem("doctorSchedules")) || [];
const saveSchedules = (schedules) => localStorage.setItem("doctorSchedules", JSON.stringify(schedules));

const EditSchedule = ({ reset, event, onUpdate }) => {
  const [state, setState] = useState({ deleted: false, loadingSpinner: false });
  const safeEvent = event || {};

  useEffect(() => {
    if (!event) reset();
  }, [event, reset]);

  const formik = useFormik({
    initialValues: {
      purpose: safeEvent.purpose || "",
      clinicName: safeEvent.clinicName || "",
      startTime: safeEvent.startTime || "",
      endTime: safeEvent.endTime || "",
      stDate: safeEvent.stDate || "",
      endDate: safeEvent.endDate || "",
    },
    validateOnBlur: true,
    validationSchema: Yup.object({
      purpose: Yup.string().required("Required"),
      clinicName: Yup.string().required("Required"),
      startTime: Yup.string().required("Required"),
      endTime: Yup.string().required("Required"),
      stDate: Yup.string().required("Required"),
      endDate: Yup.string().required("Required"),
    }),
    onSubmit: (values) => handleUpdate(values),
  });

  const handleUpdate = useCallback(
    (values) => {
      if (!event) return;
      setState((prev) => ({ ...prev, loadingSpinner: true }));
      const schedules = getSchedules();
      const index = schedules.findIndex((sch) => sch.id === event.id);
      if (index === -1) {
        toast.error("Schedule not found");
        setState((prev) => ({ ...prev, loadingSpinner: false }));
        return;
      }
      schedules[index] = { ...schedules[index], ...values, updatedAt: new Date().toISOString() };
      saveSchedules(schedules);
      toast.success("Schedule updated");
      setState((prev) => ({ ...prev, loadingSpinner: false }));
      if (onUpdate) onUpdate();
      reset();
    },
    [event, reset, onUpdate]
  );

  const handleDelete = () => {
    if (!event) return;
    toggleState("deleted", true, setState);
  };

  const confirmDelete = useCallback(() => {
    if (!event) return;
    setState((prev) => ({ ...prev, loadingSpinner: true }));
    const schedules = getSchedules().filter((sch) => sch.id !== event.id);
    saveSchedules(schedules);
    toast.success("Schedule deleted");
    setState((prev) => ({ ...prev, loadingSpinner: false, deleted: false }));
    if (onUpdate) onUpdate();
    reset();
  }, [event, reset, onUpdate]);

  const cancelDelete = () => toggleState("deleted", false, setState);
  const handleResetDelete = () => {
    toggleState("deleted", false, setState);
    reset();
  };

  if (!event) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/30 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <h3 className="text-xl font-bold text-gray-800">Edit Schedule</h3>
            <button onClick={reset} className="p-1 rounded-full hover:bg-gray-100 transition">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <form onSubmit={formik.handleSubmit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Purpose", name: "purpose", type: "text" },
                { label: "Location", name: "clinicName", type: "text" },
                { label: "Start Time", name: "startTime", type: "time" },
                { label: "End Time", name: "endTime", type: "time" },
                { label: "Start Date", name: "stDate", type: "date" },
                { label: "End Date", name: "endDate", type: "date" },
              ].map(({ label, name, type }) => (
                <div key={name} className="col-span-2 sm:col-span-1">
                  <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                  </label>
                  <input
                    type={type}
                    id={name}
                    name={name}
                    value={formik.values[name]}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  />
                  {formik.touched[name] && formik.errors[name] && (
                    <p className="text-red-500 text-xs mt-1">{formik.errors[name]}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                type="button"
                onClick={handleDelete}
                className="flex-1 py-2.5 px-4 bg-red-100 text-red-700 hover:bg-red-200 font-semibold rounded-xl transition"
              >
                Delete
              </button>
              <button
                type="submit"
                className="flex-1 py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition"
              >
                Update
              </button>
            </div>
          </form>
        </div>
      </div>

      {state.deleted && (
        <ModalSelect
          reset={handleResetDelete}
          event={event}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
      <ToastContainer />
      {state.loadingSpinner && <Spinner />}
    </>
  );
};

export default EditSchedule;